// Functional Roadmap
// Hierarchical table view (Goal -> Feature -> Milestone) using TanStack Table

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getExpandedRowModel,
    flexRender,
    type ColumnDef,
    type ExpandedState,
} from '@tanstack/react-table';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronRight, ChevronDown, Plus, Edit2, Trash2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useRoadmap } from '../shared/RoadmapContext';
import { funcApi } from '../shared/api';
import { STATUS_COLORS } from '../shared/constants';
import type { RoadmapStatus, Effort, FuncMilestone } from '../shared/types';
import { toast } from 'sonner';
import { LoadingState } from '@/components/ds';

// Flattened data structure for the table
type RoadmapNode = {
    id: string;
    type: 'goal' | 'feature' | 'milestone';
    title: string;
    description?: string;
    status?: string;
    effort?: string;
    dueDate?: string;
    completedAt?: string;
    timeline?: string;
    progress?: number;
    parentId?: string;
    subRows?: RoadmapNode[];
    originalId: string; // ID in DB
};

export function FunctionalRoadmap() {
    const { user } = useAuth();
    const { roadmap } = useRoadmap();
    const [data, setData] = useState<RoadmapNode[]>([]);
    const [expanded, setExpanded] = useState<ExpandedState>({});
    const [loading, setLoading] = useState(true);

    // Dialog states
    const [isAddingGoal, setIsAddingGoal] = useState(false);
    const [isAddingFeature, setIsAddingFeature] = useState<string | null>(null); // goalId
    const [isAddingMilestone, setIsAddingMilestone] = useState<string | null>(null); // featureId
    const [editingFeature, setEditingFeature] = useState<RoadmapNode | null>(null);
    const [newItemTitle, setNewItemTitle] = useState('');
    const [newMilestoneDue, setNewMilestoneDue] = useState('');

    // Delete confirmation state (FUNC-7)
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; originalId: string; type: 'goal' | 'feature' | 'milestone'; title: string } | null>(null);

    // Inline rename state (FUNC-8)
    const [editingTitleId, setEditingTitleId] = useState<string | null>(null);
    const [editingTitleValue, setEditingTitleValue] = useState('');

    // Load data
    const loadData = useCallback(async () => {
        if (!user || !roadmap) return;
        setLoading(true);
        try {
            const goalsRes = await funcApi.listGoals(roadmap.id);
            if (goalsRes.data) {
                // FUNC-11: Parallelize goal fetches with Promise.all
                const treeData: RoadmapNode[] = await Promise.all(
                    goalsRes.data.map(async (goal) => {
                        const { features } = await funcApi.getGoalWithFeatures(goal.id);
                        const featureNodes: RoadmapNode[] = await Promise.all(
                            (features?.data || []).map(async (f) => {
                                const milestonesRes = await funcApi.listMilestones(f.id);
                                // FUNC-6: include completedAt in milestone nodes
                                const milestoneNodes: RoadmapNode[] = (milestonesRes.data || []).map((m: FuncMilestone) => ({
                                    id: `ms-${m.id}`,
                                    originalId: m.id,
                                    type: 'milestone' as const,
                                    title: m.title,
                                    dueDate: m.due_date ?? undefined,
                                    completedAt: m.completed_at ?? undefined,
                                }));
                                return {
                                    id: `feat-${f.id}`,
                                    originalId: f.id,
                                    type: 'feature' as const,
                                    title: f.title,
                                    description: f.description,
                                    effort: f.effort,
                                    status: f.status,
                                    orderIndex: f.order_index,
                                    subRows: milestoneNodes,
                                };
                            })
                        );
                        console.log(`DEBUG: Processed ${featureNodes.length} features for goal ${goal.id}`);

                        // FUNC-10: Compute goal progress rollup
                        const totalFeatures = featureNodes.length;
                        const completedFeatures = featureNodes.filter(f => f.status === 'completed').length;
                        const progress = totalFeatures > 0 ? Math.round((completedFeatures / totalFeatures) * 100) : undefined;

                        return {
                            id: `goal-${goal.id}`,
                            type: 'goal' as const,
                            title: goal.title,
                            // FUNC-12: surface goal description so the field isn't hidden
                            description: goal.description ?? undefined,
                            originalId: goal.id,
                            // FUNC-9: Goal timeline
                            timeline: [goal.target_quarter, goal.target_year].filter(Boolean).join(' ') || undefined,
                            progress,
                            subRows: featureNodes,
                        };
                    })
                );
                setData(treeData);
                // FUNC-5: Default-expand all rows on load
                setExpanded(true);
            }
        } catch (error) {
            console.error('Failed to load functional roadmap:', error);
            toast.error('Failed to load functional roadmap');
        } finally {
            setLoading(false);
        }
    }, [user, roadmap]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Handle addition
    const handleAddGoal = async () => {
        if (!newItemTitle.trim() || !user || !roadmap) return;

        const { error } = await funcApi.createGoal({
            owner_id: user.id,
            roadmap_id: roadmap.id,
            title: newItemTitle
        });

        if (error) {
            toast.error('Failed to create goal');
            return;
        }

        setNewItemTitle('');
        setIsAddingGoal(false);
        loadData();
        toast.success('Goal created');
    };

    const handleAddFeature = async () => {
        if (!isAddingFeature || !newItemTitle.trim()) return;

        const { error } = await funcApi.createFeature({ goal_id: isAddingFeature, title: newItemTitle });

        if (error) {
            toast.error('Failed to create feature');
            return;
        }

        setNewItemTitle('');
        setIsAddingFeature(null);
        loadData();
        toast.success('Feature created');
    };

    const handleAddMilestone = async () => {
        if (!isAddingMilestone || !newItemTitle.trim()) return;

        const { error } = await funcApi.createMilestone({
            feature_id: isAddingMilestone,
            title: newItemTitle,
            due_date: newMilestoneDue || undefined,
        });

        if (error) {
            toast.error('Failed to create milestone');
            return;
        }

        setNewItemTitle('');
        setNewMilestoneDue('');
        setIsAddingMilestone(null);
        loadData();
        toast.success('Milestone created');
    };

    // FUNC-7: Handle delete confirmation
    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;
        const { originalId, type } = deleteTarget;
        try {
            if (type === 'goal') {
                await funcApi.deleteGoal(originalId);
            } else if (type === 'feature') {
                await funcApi.deleteFeature(originalId);
            } else {
                await funcApi.deleteMilestone(originalId);
            }
            toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted`);
            loadData();
        } catch {
            toast.error(`Failed to delete ${type}`);
        } finally {
            setDeleteTarget(null);
        }
    };

    // FUNC-8: Handle inline rename save
    const handleSaveTitle = async (item: RoadmapNode) => {
        const trimmed = editingTitleValue.trim();
        if (!trimmed) {
            setEditingTitleId(null);
            return;
        }
        try {
            if (item.type === 'goal') {
                await funcApi.updateGoal(item.originalId, { title: trimmed });
            } else if (item.type === 'feature') {
                await funcApi.updateFeature(item.originalId, { title: trimmed });
            } else {
                await funcApi.updateMilestone(item.originalId, { title: trimmed });
            }
            setEditingTitleId(null);
            loadData();
        } catch {
            toast.error('Failed to rename');
            setEditingTitleId(null);
        }
    };

    // Columns definition
    // Column order: Title | Status | Effort | Due Date | Timeline | Progress | Actions
    const columns = useMemo<ColumnDef<RoadmapNode>[]>(() => [
        {
            accessorKey: 'title',
            header: 'Roadmap Item',
            cell: ({ row }) => {
                const item = row.original;
                const type = item.type;
                const isEditing = item.id === editingTitleId;
                return (
                    <div
                        className="flex items-center gap-2"
                        style={{ paddingLeft: `${row.depth * 20}px` }}
                    >
                        {row.getCanExpand() ? (
                            <button
                                onClick={row.getToggleExpandedHandler()}
                                className="p-1 hover:bg-muted rounded"
                            >
                                {row.getIsExpanded() ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </button>
                        ) : (
                            <div className="w-6" /> // spacer
                        )}

                        {/* Type Indicator */}
                        {type === 'goal' && <span className="p-1 bg-blue-100 text-blue-700 rounded text-xs font-bold uppercase tracking-wider">Goal</span>}
                        {type === 'feature' && <span className="p-1 bg-green-100 text-green-700 rounded text-xs font-bold uppercase tracking-wider">Feat</span>}
                        {type === 'milestone' && <span className="p-1 bg-orange-100 text-orange-700 rounded text-xs font-bold uppercase tracking-wider">Mile</span>}

                        {/* FUNC-8: Inline rename */}
                        {isEditing ? (
                            <Input
                                autoFocus
                                className="h-7 text-sm px-2 py-0"
                                value={editingTitleValue}
                                onChange={(e) => setEditingTitleValue(e.target.value)}
                                onBlur={() => handleSaveTitle(item)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleSaveTitle(item);
                                    } else if (e.key === 'Escape') {
                                        e.preventDefault();
                                        setEditingTitleId(null);
                                    }
                                }}
                            />
                        ) : (
                            <div className="flex flex-col min-w-0">
                                <span
                                    className={
                                        (type === 'goal' ? 'font-bold text-lg' : type === 'feature' ? 'font-medium' : 'text-sm text-muted-foreground') +
                                        (type === 'milestone' && item.completedAt ? ' line-through opacity-60' : '')
                                    }
                                    onDoubleClick={() => {
                                        setEditingTitleId(item.id);
                                        setEditingTitleValue(item.title);
                                    }}
                                >
                                    {item.title}
                                </span>
                                {/* FUNC-12: show goal/feature description (was hidden in the schema) */}
                                {item.description && (type === 'goal' || type === 'feature') && (
                                    <span className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                                        {item.description}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            accessorKey: 'status',
            header: 'Status',
            cell: ({ row }) => {
                const status = row.original.status as RoadmapStatus | undefined;
                if (!status) return null;
                return (
                    <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_COLORS[status] }} />
                        <span className="capitalize text-sm">{status.replace('_', ' ')}</span>
                    </div>
                );
            },
        },
        {
            accessorKey: 'effort',
            header: 'Effort',
            cell: ({ row }) => {
                const effort = row.original.effort;
                if (!effort) return null;
                return <span className="text-sm text-muted-foreground uppercase">{effort}</span>;
            }
        },
        {
            accessorKey: 'dueDate',
            header: 'Due Date',
            cell: ({ row }) => {
                const due = row.original.dueDate;
                if (!due) return null;
                return (
                    <span className="text-sm text-muted-foreground">
                        {new Date(due).toLocaleDateString()}
                    </span>
                );
            }
        },
        // FUNC-9: Goal Timeline column
        {
            accessorKey: 'timeline',
            header: 'Timeline',
            cell: ({ row }) => {
                if (row.original.type !== 'goal' || !row.original.timeline) return null;
                return <span className="text-sm text-muted-foreground">{row.original.timeline}</span>;
            }
        },
        // FUNC-10: Goal Progress column
        {
            accessorKey: 'progress',
            header: 'Progress',
            cell: ({ row }) => {
                const p = row.original.progress;
                if (row.original.type !== 'goal' || p === undefined) return null;
                return (
                    <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden" style={{ minWidth: 60 }}>
                            <div className="h-full bg-primary rounded-full" style={{ width: `${p}%` }} />
                        </div>
                        <span className="text-xs text-muted-foreground w-8">{p}%</span>
                    </div>
                );
            }
        },
        {
            header: 'Actions',
            cell: ({ row }) => {
                const item = row.original;
                return (
                    <div className="flex items-center gap-2">
                        {/* FUNC-6: Milestone completion checkbox */}
                        {item.type === 'milestone' && (
                            <label className="flex items-center gap-1 cursor-pointer text-sm text-muted-foreground">
                                <input
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-muted-foreground accent-primary cursor-pointer"
                                    checked={!!item.completedAt}
                                    onChange={async (e) => {
                                        e.stopPropagation();
                                        await funcApi.updateMilestone(item.originalId, {
                                            completed_at: e.target.checked ? new Date().toISOString() : null,
                                        });
                                        loadData();
                                    }}
                                />
                                Done
                            </label>
                        )}

                        {item.type === 'goal' && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); setIsAddingFeature(item.originalId); }}
                                className="h-6"
                            >
                                <Plus className="h-3 w-3 mr-1" /> Add Feature
                            </Button>
                        )}
                        {item.type === 'feature' && (
                            <>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => { e.stopPropagation(); setIsAddingMilestone(item.originalId); }}
                                    className="h-6"
                                >
                                    <Plus className="h-3 w-3 mr-1" /> Add Milestone
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => { e.stopPropagation(); setEditingFeature(item); }}
                                    className="h-6 text-muted-foreground hover:text-foreground"
                                >
                                    <Edit2 className="h-3 w-3" />
                                </Button>
                            </>
                        )}

                        {/* FUNC-7: Delete button for all row types */}
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                                e.stopPropagation();
                                setDeleteTarget({ id: item.id, originalId: item.originalId, type: item.type, title: item.title });
                            }}
                            className="h-6 text-muted-foreground hover:text-destructive"
                        >
                            <Trash2 className="h-3 w-3" />
                        </Button>
                    </div>
                );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [editingTitleId, editingTitleValue]);

    const table = useReactTable({
        data,
        columns,
        state: { expanded },
        onExpandedChange: setExpanded,
        getSubRows: (row) => row.subRows,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
    });

    if (loading) return <LoadingState label="Loading functional roadmap..." />;

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">Functional Roadmap</h2>

                {/* Add Goal Dialog */}
                <Dialog open={isAddingGoal} onOpenChange={setIsAddingGoal}>
                    <DialogTrigger asChild>
                        <Button>
                            <Plus className="h-4 w-4 mr-2" />
                            Add Goal
                        </Button>
                    </DialogTrigger>
                    <DialogContent aria-describedby={undefined}>
                        <DialogHeader>
                            <DialogTitle>New Strategic Goal</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                            <Input
                                placeholder="Goal Title"
                                value={newItemTitle}
                                onChange={(e) => setNewItemTitle(e.target.value)}
                            />
                            <Button onClick={handleAddGoal} className="w-full">Create Goal</Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Add Feature Dialog */}
            <Dialog open={!!isAddingFeature} onOpenChange={(o) => !o && setIsAddingFeature(null)}>
                <DialogContent aria-describedby={undefined}>
                    <DialogHeader>
                        <DialogTitle>Add Feature to Goal</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <Input
                            placeholder="Feature Title"
                            value={newItemTitle}
                            onChange={(e) => setNewItemTitle(e.target.value)}
                        />
                        <Button onClick={handleAddFeature} className="w-full">Create Feature</Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Add Milestone Dialog */}
            <Dialog open={!!isAddingMilestone} onOpenChange={(o) => !o && setIsAddingMilestone(null)}>
                <DialogContent aria-describedby={undefined}>
                    <DialogHeader>
                        <DialogTitle>Add Milestone</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Title</Label>
                            <Input
                                placeholder="Milestone title"
                                value={newItemTitle}
                                onChange={(e) => setNewItemTitle(e.target.value)}
                            />
                        </div>
                        <div>
                            <Label>Due Date (optional)</Label>
                            <Input
                                type="date"
                                value={newMilestoneDue}
                                onChange={(e) => setNewMilestoneDue(e.target.value)}
                            />
                        </div>
                        <Button onClick={handleAddMilestone} className="w-full">Create Milestone</Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Edit Feature Dialog */}
            <Dialog open={!!editingFeature} onOpenChange={(o) => !o && setEditingFeature(null)}>
                <DialogContent aria-describedby={undefined}>
                    <DialogHeader>
                        <DialogTitle>Edit Feature</DialogTitle>
                    </DialogHeader>
                    {editingFeature && (
                        <div className="space-y-4">
                            <div>
                                <Label>Status</Label>
                                <Select
                                    value={editingFeature.status}
                                    onValueChange={(v) => setEditingFeature({ ...editingFeature, status: v })}
                                >
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="planned">Planned</SelectItem>
                                        <SelectItem value="in_progress">In Progress</SelectItem>
                                        <SelectItem value="completed">Completed</SelectItem>
                                        <SelectItem value="blocked">Blocked</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Effort</Label>
                                <Select
                                    value={editingFeature.effort}
                                    onValueChange={(v) => setEditingFeature({ ...editingFeature, effort: v })}
                                >
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="xs">XS</SelectItem>
                                        <SelectItem value="small">Small</SelectItem>
                                        <SelectItem value="medium">Medium</SelectItem>
                                        <SelectItem value="large">Large</SelectItem>
                                        <SelectItem value="xlarge">XL</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <Button onClick={async () => {
                                if (editingFeature) {
                                    const { error } = await funcApi.updateFeature(editingFeature.originalId, {
                                        status: editingFeature.status as RoadmapStatus,
                                        effort: editingFeature.effort as Effort
                                    });

                                    if (error) {
                                        toast.error('Failed to update feature');
                                    } else {
                                        toast.success('Feature updated');
                                    }
                                }
                                setEditingFeature(null);
                                loadData();
                            }} className="w-full">Save Changes</Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <Card className="p-4">
                <table className="w-full">
                    <thead>
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id} className="border-b">
                                {headerGroup.headers.map(header => (
                                    <th key={header.id} className="text-left p-2 font-medium text-muted-foreground">
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map(row => (
                            <tr key={row.id} className="border-b hover:bg-muted/10 group">
                                {row.getVisibleCells().map(cell => (
                                    <td key={cell.id} className="p-3">
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>

                {data.length === 0 && (
                    <div className="text-center py-10 text-muted-foreground">
                        No goals defined. Create your first goal to get started.
                    </div>
                )}
            </Card>

            {/* FUNC-7: Delete confirmation AlertDialog */}
            <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Delete {deleteTarget?.type ? deleteTarget.type.charAt(0).toUpperCase() + deleteTarget.type.slice(1) : ''}?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            "{deleteTarget?.title}" will be permanently deleted.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleConfirmDelete}>Delete</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

export default FunctionalRoadmap;
